import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Input, Select, Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

/**
 * 077: the areas the form offers. UI wording, not reference data — `area` is a plain string on the
 * record, so this list changes without a migration, an index or a seed (research R7).
 *
 * Exported so the form and its tests assert against ONE source and cannot drift: a test carrying its
 * own literal list still passes while the form offers something else, which is precisely the
 * comparability claim (FR-002, SC-004) that the whole feature rests on.
 */
export const INSPECTION_AREAS = [
  'Front bumper',
  'Rear bumper',
  'Driver door',
  'Passenger door',
  'Windscreen',
  'Wheels / tyres',
  'Interior',
] as const;

/** The escape hatch for damage the list does not name (FR-003, US3). Never sent as an `area`. */
export const OTHER_AREA = 'Other';

/** FR-004. The record's own enum — `inspectionItemSchema` (`bookings/routes.ts:138-145`). */
export const INSPECTION_CONDITIONS = ['excellent', 'good', 'fair', 'poor'] as const;

/** `inspectionItemSchema.notes` is `.max(500)` — refused here so nothing typed is lost to a 400. */
export const FINDING_NOTES_MAX = 500;

/** `inspectionItemSchema.area` is `.min(1).max(100)`. */
export const AREA_MAX = 100;

/**
 * 305 (FR-012): the only types the platform accepts for an inspection photo
 * (`files/routes.ts`, `inspection_photo`). Checked here too so a refusal names the file, instead of
 * surfacing as a bare 400 after the upload.
 */
export const INSPECTION_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** 305: one chosen photo, uploaded as soon as it is chosen so a failure points at THIS photo. */
interface PhotoUpload {
  key: number;
  file: File;
  status: 'uploading' | 'done' | 'failed';
  /** The uploaded asset's id — set only once `status` is `done`. */
  id?: string;
}

interface Finding {
  /** Render key only. Never sent — the record has no id for a finding until the inspection saves. */
  key: number;
  /** One of `INSPECTION_AREAS`, or `OTHER_AREA` while a typed name is being entered. */
  area: string;
  /** Only meaningful while `area === OTHER_AREA`. Never sent under that name (FR-003). */
  otherArea: string;
  condition: string;
  notes: string;
  /** 305 (FR-011): photos attached to this finding. */
  photos: PhotoUpload[];
  /** 305: files refused before upload (wrong type), named so the user knows which. */
  refused: string[];
}

/**
 * The name this finding is recorded under. `OTHER_AREA` is a choice in the select, never a part —
 * an inspection reading "Other: poor" names nothing. Trimmed (FR-009, mutation M3) so that
 * `" Wing mirror "` and `"Wing mirror"` do not become two parts in the data.
 */
function resolveArea(finding: Finding): string {
  return finding.area === OTHER_AREA ? finding.otherArea.trim() : finding.area;
}

interface InspectionFormModalProps {
  bookingId: string;
  inspectionType: 'checkout' | 'checkin';
  onClose: () => void;
  /** 311 (#549): called once the save has SUCCEEDED, before the modal closes. */
  onSaved?: () => void;
}

export function InspectionFormModal({
  bookingId,
  inspectionType,
  onClose,
  onSaved,
}: InspectionFormModalProps) {
  const queryClient = useQueryClient();
  const [odometer, setOdometer] = useState('');
  const [fuelLevel, setFuelLevel] = useState('100');
  const [overallCondition, setOverallCondition] = useState('good');
  const [notes, setNotes] = useState('');
  const [findings, setFindings] = useState<Finding[]>([]);
  const [findingError, setFindingError] = useState<string | null>(null);
  const nextKey = useRef(0);
  const nextPhotoKey = useRef(0);

  function patchPhoto(findingKey: number, photoKey: number, patch: Partial<PhotoUpload>) {
    setFindings((current) =>
      current.map((f) =>
        f.key === findingKey
          ? { ...f, photos: f.photos.map((ph) => (ph.key === photoKey ? { ...ph, ...patch } : ph)) }
          : f,
      ),
    );
  }

  /** Upload one photo; its row reflects the outcome. Keyed, never indexed, so a removal elsewhere
   *  cannot redirect a late-finishing upload onto the wrong photo. */
  async function uploadPhoto(findingKey: number, photoKey: number, file: File) {
    patchPhoto(findingKey, photoKey, { status: 'uploading', id: undefined });
    try {
      const form = new FormData();
      // `file` before `purpose` — load-bearing for a real streamed multipart body (210 R4).
      form.append('file', file, file.name);
      form.append('purpose', 'inspection_photo');
      const result = (await api.upload('/files/upload', form)) as { id: string };
      patchPhoto(findingKey, photoKey, { status: 'done', id: result.id });
    } catch {
      patchPhoto(findingKey, photoKey, { status: 'failed' });
    }
  }

  function choosePhotos(findingKey: number, files: File[]) {
    const accepted = files.filter((f) =>
      (INSPECTION_PHOTO_TYPES as readonly string[]).includes(f.type),
    );
    const refused = files.filter((f) => !accepted.includes(f)).map((f) => f.name);
    const added = accepted.map((file) => ({
      key: nextPhotoKey.current++,
      file,
      status: 'uploading' as const,
    }));
    setFindings((current) =>
      current.map((f) =>
        f.key === findingKey ? { ...f, photos: [...f.photos, ...added], refused } : f,
      ),
    );
    for (const photo of added) void uploadPhoto(findingKey, photo.key, photo.file);
  }

  function removePhoto(findingKey: number, photoKey: number) {
    setFindings((current) =>
      current.map((f) =>
        f.key === findingKey ? { ...f, photos: f.photos.filter((ph) => ph.key !== photoKey) } : f,
      ),
    );
  }

  // FR-013: nothing is saved while a photo is still on its way or has failed — an inspection saved
  // without a photo the user attached would silently lose the evidence they meant to record.
  const photosPending = findings.some((f) => f.photos.some((ph) => ph.status !== 'done'));

  function updateFinding(index: number, patch: Partial<Finding>) {
    setFindings((current) => current.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  }

  /**
   * FR-008. The platform would refuse an arealess or conditionless finding anyway — both are
   * required by `inspectionItemSchema` — but a 400 cannot say WHICH of five findings is the
   * incomplete one, and the staff member is standing at the car. Refused here so the message can.
   */
  function firstIncompleteFinding(): string | null {
    for (const [index, finding] of findings.entries()) {
      const position = `Finding ${index + 1}`;
      // `resolveArea`, not `finding.area`: "Other" with nothing typed, and "Other" with three
      // spaces typed, are both an unnamed part (US3-3, mutation M4).
      if (!resolveArea(finding)) return `${position} needs an area before it can be saved.`;
      if (!finding.condition) return `${position} needs a condition before it can be saved.`;
      if (finding.notes.length > FINDING_NOTES_MAX)
        return `${position}'s description is longer than ${FINDING_NOTES_MAX} characters. Shorten it before saving.`;
    }
    return null;
  }

  function save() {
    if (photosPending) return;
    const incomplete = firstIncompleteFinding();
    setFindingError(incomplete);
    if (incomplete) return;
    submit.mutate();
  }

  const submit = useMutation({
    mutationFn: () => {
      const items = findings.map((f) => ({
        area: resolveArea(f),
        condition: f.condition,
        // Omitted, not empty: `notes` is optional on the record, and a blank string is not a
        // description anyone wrote.
        ...(f.notes ? { notes: f.notes } : {}),
        // 305: omitted when none — the body stays exactly what it was before photos existed.
        ...(f.photos.length > 0 ? { photo_ids: f.photos.map((ph) => ph.id as string) } : {}),
      }));
      return api.post(`/bookings/${bookingId}/inspections`, {
        inspection_type: inspectionType,
        odometer_km: odometer ? Number(odometer) : undefined,
        fuel_level_percent: Number(fuelLevel),
        overall_condition: overallCondition,
        notes: notes || undefined,
        // FR-007: OMITTED, not `[]`, when nothing was recorded. The platform defaults `items`
        // either way, so this is invisible in the stored document and in every test that inspects
        // the result — it is observable only in this body. Mutation M2 exists for this line.
        ...(items.length > 0 ? { items } : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.one(bookingId) });
      // H13: the inspections list is its own query. Without this, a just-recorded inspection stays
      // invisible until a full reload — indistinguishable from a broken endpoint.
      void queryClient.invalidateQueries({ queryKey: ['booking-inspections', bookingId] });
      onSaved?.();
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={<span className="capitalize">{inspectionType} Inspection</span>}
      size="md"
      scrollBody
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={submit.isPending} disabled={photosPending} onClick={save}>
            Save inspection
          </Button>
        </>
      }
    >
      {/* 🔴 Measured in a browser during Gate A, not argued: with three findings this panel is
          1298px tall in a 904px viewport, the page behind a fixed overlay does not scroll, and
          "Save inspection" sits at y=1047 — off-screen and unreachable, so the inspection cannot be
          saved at all. Every test in the suite passed: jsdom has no layout. The body scrolls; the
          heading and the buttons stay put, so the control that saves is always on screen. */}
      <div data-testid="inspection-form-body" className="space-y-4 overflow-y-auto min-h-0">
        <div className="flex flex-col gap-1">
          <label className="text-sm text-steel" htmlFor="odometer">
            Odometer (km)
          </label>
          <Input
            id="odometer"
            type="number"
            min="0"
            placeholder="e.g. 12500"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm text-steel" htmlFor="fuel-level">
            Fuel level (%)
          </label>
          <Input
            id="fuel-level"
            type="number"
            min="0"
            max="100"
            value={fuelLevel}
            onChange={(e) => setFuelLevel(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm text-steel" htmlFor="overall-condition">
            Overall condition
          </label>
          <Select
            id="overall-condition"
            value={overallCondition}
            onChange={(e) => setOverallCondition(e.target.value)}
          >
            <option value="excellent">Excellent</option>
            <option value="good">Good</option>
            <option value="fair">Fair</option>
            <option value="poor">Poor</option>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm text-steel" htmlFor="notes">
            Notes / damage description
          </label>
          <Textarea
            id="notes"
            rows={3}
            placeholder="Describe any damage, issues, or observations…"
            className="resize-none"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-steel">Findings by area</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setFindings((current) => [
                  ...current,
                  {
                    key: nextKey.current++,
                    area: '',
                    otherArea: '',
                    condition: '',
                    notes: '',
                    photos: [],
                    refused: [],
                  },
                ])
              }
            >
              Add finding
            </Button>
          </div>

          {findings.map((finding, index) => (
            // Keyed by a client-side key, not the index: two findings may legitimately name the
            // same area with different conditions (a truthful record of two marks, not a
            // duplicate — mutation M12), and removing one must not slide the next one's typed
            // description up into its place.
            <div
              key={finding.key}
              data-testid={`finding-box-${index}`}
              className="flex flex-col gap-1 border border-hairline rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] p-3"
            >
              <div className="flex items-center justify-between">
                <label className="text-xs text-steel" htmlFor={`finding-${index}-area`}>
                  Finding {index + 1} area
                </label>
                {/* FR-006: a mark noticed and then found to be pre-existing is withdrawn here,
                      before anything is sent — not corrected afterwards against a stored record. */}
                <button
                  type="button"
                  className="text-xs text-steel underline"
                  onClick={() => setFindings((current) => current.filter((_, i) => i !== index))}
                >
                  Remove finding {index + 1}
                </button>
              </div>
              <Select
                id={`finding-${index}-area`}
                value={finding.area}
                onChange={(e) => updateFinding(index, { area: e.target.value })}
              >
                <option value="">Select an area…</option>
                {INSPECTION_AREAS.map((area) => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
                <option value={OTHER_AREA}>{OTHER_AREA}…</option>
              </Select>

              {/* Revealed only once asked for. A text box standing permanently beside the list
                    invites a typed duplicate of a part the list already names — the drift FR-002
                    exists to prevent. */}
              {finding.area === OTHER_AREA && (
                <>
                  <label className="text-xs text-steel" htmlFor={`finding-${index}-other-area`}>
                    Finding {index + 1} other area
                  </label>
                  <Input
                    id={`finding-${index}-other-area`}
                    type="text"
                    maxLength={AREA_MAX}
                    placeholder="Name the part, e.g. Wing mirror"
                    value={finding.otherArea}
                    onChange={(e) => updateFinding(index, { otherArea: e.target.value })}
                  />
                </>
              )}

              <label className="text-xs text-steel" htmlFor={`finding-${index}-condition`}>
                Finding {index + 1} condition
              </label>
              <Select
                id={`finding-${index}-condition`}
                value={finding.condition}
                onChange={(e) => updateFinding(index, { condition: e.target.value })}
              >
                <option value="">Select a condition…</option>
                {INSPECTION_CONDITIONS.map((condition) => (
                  <option key={condition} value={condition}>
                    {condition}
                  </option>
                ))}
              </Select>

              {/* 🔴 A description reaches administrators only: `internal = can('manage','Booking')`
                    (`bookings/routes.ts:502`), and only the admin block grants that — so a provider
                    can write one here and never read it back. Pre-existing (the inspection's own
                    note behaves identically) and deliberately unchanged: FR-014 forbids it, and
                    changing it would reverse 067. */}
              <label className="text-xs text-steel" htmlFor={`finding-${index}-description`}>
                Finding {index + 1} description
              </label>
              <Input
                id={`finding-${index}-description`}
                type="text"
                maxLength={FINDING_NOTES_MAX}
                placeholder="Optional — what the damage looks like"
                value={finding.notes}
                onChange={(e) => updateFinding(index, { notes: e.target.value })}
              />

              {/* 305 (US4): no `capture` — staff record from desktops, and #456's camera-only rule
                    is about customers substituting gallery photos (research R7). */}
              <label className="text-xs text-steel" htmlFor={`finding-${index}-photos`}>
                Finding {index + 1} photos
              </label>
              <input
                id={`finding-${index}-photos`}
                type="file"
                multiple
                accept={INSPECTION_PHOTO_TYPES.join(',')}
                className="text-sm text-steel"
                onChange={(e) => {
                  choosePhotos(finding.key, Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
              {finding.refused.map((name) => (
                <p
                  key={name}
                  data-testid={`finding-${index + 1}-photo-refused`}
                  className="text-xs text-red-500"
                >
                  {name} is not a JPEG, PNG or WebP image, so it was not attached.
                </p>
              ))}
              {finding.photos.length > 0 && (
                <div className="flex flex-col gap-1">
                  {finding.photos.map((photo) => (
                    <div
                      key={photo.key}
                      data-testid={`finding-${index + 1}-photo-${photo.status}`}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <span className="truncate text-ink">{photo.file.name}</span>
                      <span className="flex items-center gap-2">
                        {photo.status === 'uploading' && (
                          <span className="text-steel">Uploading…</span>
                        )}
                        {photo.status === 'done' && <span className="text-steel">Attached</span>}
                        {photo.status === 'failed' && (
                          <>
                            <span className="text-red-500">Upload failed</span>
                            <button
                              type="button"
                              className="text-steel underline"
                              onClick={() => void uploadPhoto(finding.key, photo.key, photo.file)}
                            >
                              Retry
                            </button>
                          </>
                        )}
                        {photo.status !== 'uploading' && (
                          <button
                            type="button"
                            className="text-steel underline"
                            onClick={() => removePhoto(finding.key, photo.key)}
                          >
                            Remove
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* One alert node, not two: a refusal from this form and a refusal from the platform are
              never both live — the first prevents the request that produces the second. */}
        {(findingError || submit.isError) && (
          <p role="alert" className="text-sm text-red-500">
            {findingError ??
              (submit.error as { message?: string })?.message ??
              'Failed to submit inspection'}
          </p>
        )}
      </div>
    </Modal>
  );
}
